import {APP_BASE_HREF, CommonModule} from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClientModule,
  HttpClientXsrfModule,
  provideHttpClient, withInterceptorsFromDi,
  withXsrfConfiguration
} from '@angular/common/http';
import {CSP_NONCE, importProvidersFrom, Injector, NgModule} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatCardModule} from '@angular/material/card';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatChipsModule} from '@angular/material/chips';
import {MatNativeDateModule} from '@angular/material/core';
import {MatDatepicker, MatDatepickerModule} from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatListModule } from '@angular/material/list';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule, MatIconRegistry} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatMenuModule} from '@angular/material/menu';
import {MatRadioGroup, MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatStepperModule} from '@angular/material/stepper';
import {MatTabsModule} from '@angular/material/tabs';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule, provideAnimations} from '@angular/platform-browser/animations';
import {EffectsModule} from '@ngrx/effects';
import {StoreModule} from '@ngrx/store';
import {TranslateModule} from '@ngx-translate/core';
import {ConfirmationPopoverModule} from 'angular-confirmation-popover';
import {ButtonModule} from 'primeng/button';
import {DatePickerModule} from 'primeng/datepicker';
import {CardModule} from 'primeng/card';
import {ChartModule} from 'primeng/chart';
import {CheckboxModule} from 'primeng/checkbox';
import {ColorPickerModule} from 'primeng/colorpicker';
import {NgxColorsModule} from 'ngx-colors';
import {DialogModule} from 'primeng/dialog';
import {DividerModule} from 'primeng/divider';
import {SelectModule} from 'primeng/select';
import {SplitterModule} from 'primeng/splitter';
import {DialogService, DynamicDialogModule} from 'primeng/dynamicdialog';
import {MultiSelectModule} from 'primeng/multiselect';
import {PanelMenuModule} from 'primeng/panelmenu';
import {RadioButtonModule} from 'primeng/radiobutton';
import {TableModule} from 'primeng/table';
import {ToastModule} from 'primeng/toast';
import {TooltipModule} from 'primeng/tooltip';
import {TreeTableModule} from 'primeng/treetable';
import {HttpRegInterceptor} from './_decorators/httpreg.interceptor';
import {AlertComponent} from './_directives/alert.component';
import {NgxInactivityDirective} from './_directives/ngx-inactivity.directive';
import {AuthGuard} from './_guards/auth.guard';
import {SanitizeHtmlPipe} from './_pipes/sanitizeHtml.pipe';
import {
  AlertService,
  AssessmentManagerService,
  AuthenticationService,
  ClientProfileService,
  ComplianceAreaService,
  ControlItemsService,
  FileBlobService,
  FileManagerBackendApiService1,
  IntegrationsService,
  NotificationService,
  ProjectControlItemService,
  ProjectService,
  TenantService,
  UploadService,
  UserService
} from './_services';
import {AccessRequestService} from './_services/accessRequest.service';
import {ActionItemService} from './_services/actionItem.service';
import {ApiKeyService} from './_services/apiKey.service';
import {AssessmentService} from './_services/assessment.service';
import {AuditService} from './_services/audit.service';
import {BulletinService} from './_services/bulletin.service';
import {DictionaryService} from './_services/dictionary.service';
import {DocumentRequestService} from './_services/documentRequest.service';
import {ImplementationControlStatementService} from './_services/implementationControlStatement.service';
import {IncidentResponseService} from './_services/incidentResponse.service';
import {IndexConfigService} from './_services/index.config.service';
import {LogService} from './_services/log.service';
import {MappingService} from './_services/mapping.service';
import {MfaService} from './_services/mfa.service';
import {PolicyService} from './_services/policy.service';
import {RecommendedSolutionService} from './_services/recommendedSolution.service';
import {ReferenceResourceService} from './_services/referemceResource.service';
import {RiskService} from './_services/risk.service';
import {SapProfileService} from './_services/sapProfile.service';
import {SarProfileService} from './_services/sarProfile.service';
import {SignalRService} from './_services/signalR.service';
import { TenantPermissionsService } from './_services/tenantPermissions.service';
import { TerminalService } from './_services/terminal.service';
import {TrainingService} from './_services/training.service';
import {WorkstreamService} from './_services/workstream.service';
import {AppComponent} from './app.component';
import {routing} from './app.routing';
import {AccountCreatedMessageComponent} from './components/accountCreatedMessage';
import {ActionItemTemplateComponent} from './components/actionItemTemplate/actionItemTemplate.component';
import {
  AddLinkRecommendedSolutionComponent
} from './components/addLinkRecommendedSolution/add-link-recommended-solution.component';
import {
  AddLinkReferenceResourceComponent
} from './components/addLinkRefrenceResource/add-link-reference-resource.component';
import {AddUrlComponent} from './components/addUrl/add-url.component';
import {AlertsComponent} from './components/alerts';
import {ApiKeysComponent} from './components/apiKeys';
import {AskStringComponent} from './components/askString/askString.component';
import { AssessmentComponent } from './components/assessment/assessment.component';
import { ThirdPartyAssessmentComponent } from './components/thirdPartyAssessmentFindings/thirdPartyAssessmentFindings.component';
import {AssessmentAnswersComponent} from './components/assessmentAnswers/assessmentAnswers.component';
import {AssessmentEditComponent} from './components/assessmentEdit/assessment-edit.component';
import {AssessmentFindingsComponent} from './components/assessmentFindings/assessmentFindings.component';
import {AssessmentInviteComponent} from './components/assessmentInvite/assessment-invite.component';
import {AssessmentsComponent} from './components/assessments/assessments.component';
import {AssessmentSummaryComponent} from './components/assessmentSummary/assessmentSummary.component';
import { AssessmentUserAnswersComponent } from './components/assessmentUserAnswers/assessmentUserAnswers.component';
import { AssessmentManagerEditComponent, AssessmentManagerListComponent, AssessmentManagerViewComponent } from './components/assessmentManager';
import {AuditComponent} from './components/audit/audit.component';
import {AzureComponent} from './components/azure/azure.component';
import {BulletinComponent} from './components/bulletin';
import {BulletinsComponent} from './components/bulletins/bulletins.component';
import {ChangePasswordComponent} from './components/changePassword';
import {ChangePasswordPopupComponent} from './components/changePasswordPopup';
import {CidReportComponent} from './components/cidReport';
import {ClientProfileComponent} from './components/clientProfile';
import {LicenseComponent} from './components/clientProfile/license/license.component';
import {CloneProjectComponent} from './components/cloneProject/cloneProject.component';
import {CloneSourceComponent} from './components/cloneSource/cloneSource.component';
import {ConfirmComponent} from './components/confirm';
import {ControlItemComponent} from './components/controlItem';
import {ControlItemsComponent} from './components/controlItems';
import {ControlItemUpdatesComponent} from './components/controlItemUpdates';
import {CreateBulletinComponent} from './components/createBulletin';
import {CreatePoamTemplateComponent} from './components/createPoamTemplate';
import {CreateProjectComponent} from './components/createProject';
import {CreateTenantComponent} from './components/createTenant';
import {DictionaryComponent} from './components/dictionary/dictionary.component';
import {DocumentsComponent} from './components/documents';
import {DocumentSelectComponent} from './components/documentSelect';
import {EditIntegrationAppComponent} from './components/edit-integration-app/edit-integration-app.component';
import {EditRiskComponent} from './components/edit-risk/edit-risk.component';
import {EditBulletinComponent} from './components/editBulletin';
import {EditComplianceAreaComponent} from './components/editComplianceArea/edit.complianceArea.component';
import {EditDictionaryItemComponent} from './components/editDictionaryItem/editDictionaryItem.component';
import {EditDocumentRequestComponent} from './components/editDocumentRequest/editDocumentRequest.component';
import {EditIncidentResponseComponent} from './components/editIncidentResponse/editIncidentResponse.component';
import {ExportPolicyComponent} from './components/exportPolicy/exportPolicy.component';
import {ForgotPasswordComponent} from './components/forgotPassword';
import {GenerateCrmComponent} from './components/generateCrm/generateCrm.component';
import {GenerateSspComponent} from './components/generateSsp/generateSsp.component';
import {HeaderComponent} from './components/header/header.component';
import {HomeComponent} from './components/home/home.component';
import {
  ImplementationControlStatementComponent
} from './components/implementationControlStatement/implementation-control-statement.component';
import {ImportSspComponent} from './components/importSsp/importSsp.component';
import {InactivityMessageComponent} from './components/inactivityMessage';
import {IncidentResponseComponent} from './components/incidentResponse/incidentResponse.component';
import {InitializeTenantComponent} from './components/initializeTenant/initializeTenant.component';
import {InqSsoComponent} from './components/inqSso/inqSso.component';
import {
  IntegrationAppsEvidenceComponent
} from './components/integration-apps-evidence/integration-apps-evidence.component';
import {
  IntegrationAppsOscalCatalogComponent
} from './components/integration-apps-oscal-catalog/integration-apps-oscal-catalog.component';
import {
  IntegrationAppsOscalImplementationComponent
} from './components/integration-apps-oscal-implementation/integration-apps-oscal-implementation.component';
import {
  IntegrationAppsOscalPoamComponent
} from './components/integration-apps-oscal-poam/integration-apps-oscal-poam.component';
import {
  IntegrationAppsOscalProfileCategoryComponent
} from './components/integration-apps-oscal-profile-category/integration-apps-oscal-profile-category.component';
import {
  IntegrationAppsOscalProfileComponent
} from './components/integration-apps-oscal-profile/integration-apps-oscal-profile.component';
import {
  IntegrationAppsOscalSapComponent
} from './components/integration-apps-oscal-sap/integration-apps-oscal-sap.component';
import {
  IntegrationAppsOscalSarComponent
} from './components/integration-apps-oscal-sar/integration-apps-oscal-sar.component';
import {IntegrationAppsPoamComponent} from './components/integration-apps-poam/integration-apps-poam.component';
import {IntegrationAppsComponent} from './components/integration-apps/integration-apps.component';
import {IntegrationsComponent} from './components/integrations';
import {InviteSurveyUserComponent} from './components/inviteSurveyUser/invite.survey-user.component';
import {InviteUserComponent} from './components/inviteUser';
import {ItProfileComponent} from './components/itProfile';
import {ItProfileTemplateEditComponent} from './components/itProfileTemplateEdit/it-profile-template-edit.component';
import {ListMessageComponent} from './components/listMessage';
import {LogComponent} from './components/log/log.component';
import {LoginComponent} from './components/login';
import {MapControlItemsComponent} from './components/mapControlItems/mapControlItems.component';
import {MappingTemplateComponent} from './components/mappingTemplate/mappingTemplate.component';
import {MapProjectControlItemsComponent} from './components/mapProjectControlItems';
import {MapWorkstreamItemsComponent} from './components/mapWorkstreamItems';
import {MessageComponent} from './components/message';
import {SetupEmailMfaComponent} from './components/mfa/setupEmail/setup-email.component';
import {SetupTotpComponent} from './components/mfa/setupTotp/setup-totp.component';
import {MfaStatusComponent} from './components/mfa/status/mfa-status.component';
import {MultiLoginComponent} from './components/multi-login/multi-login.component';
import {MyProfileComponent} from './components/myProfile/my-profile.component';
import {PasswordGeneratedComponent} from './components/passwordGenerated/password.generated.component';
import {PoamComponent} from './components/poam';
import {PolicyComponent} from './components/policy/policy.component';
import {ProjectComponent} from './components/project';
import {ProjectAccessRightsComponent} from './components/projectAccessRights/project.access.rights.component';
import {ProjectControlItemComponent} from './components/projectControlItem';
import {ProjectNewComponent} from './components/projectNew/projectNew.component';
import {ProjectPoamsComponent} from './components/projectPoams/projectPoams.component';
import {ProjectPoliciesComponent} from './components/projectPolicies/projectPolicies.component';
import {ProjectPropertiesComponent} from './components/projectProperties';
import {ProjectsComponent} from './components/projects';
import {ProjectStatusComponent} from './components/projectStatus';
import {PromptComponent} from './components/prompt/prompt.component';
import {RecommendedSolutionsComponent} from './components/recommendedSolutions';
import {RecommendedSolutionsListComponent} from './components/recommendedSolutionsList';
import {RecommendedSolutionUploadComponent} from './components/recommendedSolutionUpload';
import {RedirectComponent} from './components/redirect';
import {ReferenceResourcesComponent} from './components/referenceResources';
import {ReferenceResourcesListComponent} from './components/referenceResourcesList';
import {ReferenceResourceUploadComponent} from './components/referenceResourceUpload';
import {RequestListComponent} from './components/request-list/request-list.component';
import {RiskComponent} from './components/risk/risk.component';
import {SapProfileComponent} from './components/sapProfile/sap.profile.component';
import {SarProfileComponent} from './components/sarProfile/sar.profile.component';
import {SchedulerComponent} from './components/scheduler/scheduler.component';
import {ScoringReportComponent} from './components/scoringReport/scoringReport.component';
import {
  SelectActionPlanTemplateComponent
} from './components/selectActionPlanTemplate/select-action-plan-template.component';
import {SelectAuditTemplateComponent} from './components/selectAuditTemplate/select-audit-template.component';
import {SelectControlItemComponent} from './components/selectControlItem/select-control-item.component';
import {
  SelectImplementationControlStatementComponent
} from './components/selectImplementationControlStatement/select-implementation-control-statement.component';
import {SelectMappingTemplateComponent} from './components/selectMappingTemplate/select-mapping-template.component';
import {SelectPolicyComponent} from './components/selectPolicy/select-policy.component';
import {
  SelectProjectControlItemComponent
} from './components/selectProjectControlItem/select-project-control-item.component';
import {
  SelectSingleProjectControlItemComponent
} from './components/selectSingleProjectControlItem/select-single-project-control-item.component';
import {SelectWorkstreamItemComponent} from './components/selectWorkstreamItem/select-workstream-item.component';
import {
  SharedResponsibilityReportComponent
} from './components/sharedResponsibilityReport/sharedResponsibilityReport.component';
import {SupportConsoleComponent} from './components/support-console/support-console.component';
import {SupportLoginComponent} from './components/support-login/support-login.component';
import {TenantComponent} from './components/tenant';
import {TenantPermissionsComponent} from './components/tenantPermissions';
import {TenantPermissionsEditComponent} from './components/tenantPermissionsEdit';
import {TenantsComponent} from './components/tenants';
import {TrainingResultComponent} from './components/training-results/training-result.component';
import {TrainingComponent} from './components/training/training.component';
import {TrainingEditComponent} from './components/trainingEdit/training-edit.component';
import {TrainingInviteComponent} from './components/trainingInvite/training-invite.component';
import {TrainingsComponent} from './components/trainings/trainings.component';
import {TrainingSummaryComponent} from './components/trainingSummary/trainingSummary.component';
import {TrainingUserAnswersComponent} from './components/trainingUserAnswers/trainingUserAnswers.component';
import {
  UnsubscribeNotificationComponent
} from './components/unsubscribeNotification/unsubscribe.notification.component';
import {UpdateControlsComponent} from './components/updateControls';
import {UploadComplianceAreaComponent} from './components/uploadComplianceArea';
import {UserComponent} from './components/user';
import {UserPopupComponent} from './components/userPopup';
import {UsersComponent} from './components/users';
import {WorkstreamComponent} from './components/workstream/workstream.component';
import {WorkstreamItemComponent} from './components/workstreamItem';
import {WorkstreamsComponent} from './components/workstreams';
import {WorkstreamUploadComponent} from './components/workstreamUpload';
import {IFileManagerConfiguration} from './external/angular2-filemanager/configuration/IFileManagerConfiguration';
import {FileManagerModule} from './external/angular2-filemanager/filemanager.module';
import {FileManagerApiService} from './external/angular2-filemanager/store/fileManagerApi.service';
import {FileManagerBackendApiService} from './external/angular2-filemanager/store/fileManagerBackendApi.service';
import {TreeModule} from './external/angular2-tree/tree.module';
import {PasswordStrengthMeterComponent} from './external/password-strength-meter/password-strength-meter.component';
import {PasswordStrengthMeterService} from './external/password-strength-meter/password-strength-meter.service';
import {PSMProgressBarDirective} from './external/password-strength-meter/psm-progress-bar.directive';
import {QuillEditor} from './external/primeng/editor';
import {Constants} from './helpers/constants';
import {OidcCallbackComponent} from "./components/oidcCallback/oidc-callback.component";
import {OidcService} from "./_services/oidc.service";
import {ConMonProfileComponent} from "./components/conMonProfile/con-mon.profile.component";
import {ConMonProfileService} from "./_services/conMonProfile.service";
import { IntegrationAwsConfigComponent } from "./components/integration-aws-config/integration-aws-config.component";
import { IntegrationGenericComponent } from "./components/integration-generic/integration-generic.component";
import {PoamListComponent} from "./components/poamList/poam-list.component";
import {
  SendProjectControlItemMessageComponent
} from "./components/sendProjectControlItemMessage/send-project-control-item-message.component";
import {ReplacePolicyTextComponent} from "./components/replacePolicyText/replace-policy-text.component";
import {DragDropModule} from "primeng/dragdrop";
import {EvidenceArchiveComponent} from './components/evidenceArchive';
import {EvidenceCentralComponent} from "./components/evidence-central/evidence-central.component";
import {EditEvidenceCentralComponent} from "./components/edit-evidence-central/edit-evidence-central.component";
import {EvidenceCentralService} from "./_services/evidenceCentral.service";
import {InspectionProfilesComponent} from "./components/inspectionProfiles/inspectionProfiles.component";
import {InspectionProfileService} from "./_services/inspectionProfile.service";
import {InspectionResultsPopupComponent} from "./components/inspectionResultsPopup/inspectionResultsPopup.component";
import { GuidanceDialogComponent } from './components/guidance-dialog/guidance-dialog.component';
import { ThemePalettePanelComponent } from './components/themePalettePanel/theme-palette-panel.component';
import {TrustBuilderComponent} from './components/trustBuilder/trustBuilder.component';
import {UploadFrameworkDialogComponent} from './components/uploadFrameworkDialog/uploadFrameworkDialog.component';
import {AddCategoryDialogComponent} from './components/addCategoryDialog/addCategoryDialog.component';
import {AddControlItemDialogComponent} from './components/addControlItemDialog/addControlItemDialog.component';
import { EditTrustReportDialogComponent } from './components/editTrustReportDialog/editTrustReportDialog.component';
import { RizkListConfigComponent } from './components/rizkListConfig/rizkListConfig.component';
import { RizkListReportComponent } from './components/rizkListReport/rizkListReport.component';
import {YouTubePlayerModule} from "@angular/youtube-player";
import {
  IntegrationAwsCloudTrailComponent
} from "./components/integration-aws-cloudtrail/integration-aws-cloudtrail.component";
import {EditActionItemSetComponent} from "./components/editActionItemSet/editActionItemSet.component";
import {
  EditImplementationControlStatementSetComponent
} from "./components/editImplementationControlStatementSet/editImplementationControlStatementSet.component";
import {Viewer3dComponent} from "./external/viewer-3d/viewer-3d.component";
import {FileSelectDirective} from "./external/ng2-file-upload/file-select.directive";
import {providePrimeNG} from "primeng/config";
import Aura from '@primeuix/themes/aura';
import {SelectDictionaryItemComponent} from "./components/selectDictionaryItem/select-dictionary-item.component";
import {GenericControlsComponent} from "./components/genericControls/genericControls.component";
import { GenericControlService } from "./_services/genericControl.service";
import { ApplicationConfig } from '@angular/core';
import { withInterceptors } from '@angular/common/http';
import { apiErrorInterceptor } from './core/api-error.interceptor';
import {SelectDeviceComponent} from "./components/selectDevice/select-device.component";
import {ViewFileComponent} from "./components/viewFile/viewFile.component";
import {
  SelectEvidenceCentralItemComponent
} from "./components/selectEvidenceCentralItem/select-evidenceCentralItem.component";
import {GlobalPoamListComponent} from "./components/globalPoamList/global-poam-list.component";
import {GlobalDeviceListComponent} from "./components/globalDeviceList/global-device-list.component";
import {ImportOscalComponent} from "./components/importOscal/importOscal.component";
import {CveService} from "./_services/cve.service";
import {CveComponent} from "./components/cve/cve.component";
import {IntegrationS3ConfigComponent} from "./components/integration-s3-config/integration-s3-config.component";
import {
  GlobalProjectControlItemsComponent
} from "./components/globalProjectControlItems/globalProjectControlItems.component";
import {AddSignatureComponent} from "./components/addSignature/addSignature.component";
import {TrustBuilderInviteComponent} from "./components/trustBuilderInvite/trustBuilder-invite.component";
import {GlobalTrustReportComponent} from "./components/globalTrustReport/globalTrustReport.component";
import {DocumentFolderSelect} from "./components/documentFolderSelect/documentFolderSelect.component";
//import {PdfJsViewerModule} from "ng2-pdfjs-viewer";
import { FleetComponent } from "./components/fleet/fleet.component";
import { FleetsComponent } from "./components/fleets/fleets.component";
import { FleetsDashboardComponent } from "./components/fleetsDashboard/fleets.dashboard.component";
import { FleetDashboardDetailComponent } from "./components/fleetDashboardDetail/fleet.dashboard.detail.component";
import { FleetDeviceMappingComponent } from "./components/fleetDeviceMapping/fleet.device.mapping.component";
import {SelectDiagramPositionComponent} from "./components/selectDiagramPosition/selectDiagramPosition.component";
import {TrustBuilderViewComponent} from "./components/trustBuilderView/trustBuilderView.component";

const fileManagerConfiguration: IFileManagerConfiguration = {
  allowChooseMultipleFiles: false,
  urls: {
    foldersUrl:
      (window.location.origin || Constants.DefaultApiHost) +
      '/api/documents/foldersAll', //tenantName

    filesUrl:
      (window.location.origin || Constants.DefaultApiHost) +
      '/api/documents/filesAll', //tenantName

    folderMoveUrl:
      (window.location.origin || Constants.DefaultApiHost) +
      '/api/documents/folderMove', //tenantName
  },
};

@NgModule({
  declarations: [
    AppComponent,
    AlertComponent,
    LoginComponent,
    InviteUserComponent,
    ProjectsComponent,
    CreateProjectComponent,
    ProjectComponent,
    ProjectControlItemComponent,
    UsersComponent,
    UserComponent,
    ControlItemsComponent,
    ControlItemComponent,
    HomeComponent,
    HeaderComponent,
    TenantsComponent,
    CreateTenantComponent,
    TenantComponent,
    ForgotPasswordComponent,
    ChangePasswordComponent,
    ChangePasswordPopupComponent,
    ClientProfileComponent,
    RedirectComponent,
    DocumentsComponent,
    EvidenceArchiveComponent,
    UpdateControlsComponent,
    UploadComplianceAreaComponent,
    ProjectPropertiesComponent,
    PoamComponent,
    AlertsComponent,
    InactivityMessageComponent,
    BulletinComponent,
    EditBulletinComponent,
    CreateBulletinComponent,
    PasswordGeneratedComponent,
    BulletinsComponent,
    ProjectAccessRightsComponent,
    ControlItemUpdatesComponent,
    NgxInactivityDirective,
    AccountCreatedMessageComponent,
    TrainingComponent,
    ProjectStatusComponent,
    TrainingEditComponent,
    SelectProjectControlItemComponent,
    EditComplianceAreaComponent,
    SelectControlItemComponent,
    SelectWorkstreamItemComponent,
    MapProjectControlItemsComponent,
    SelectSingleProjectControlItemComponent,
    LogComponent,
    ItProfileComponent,
    WorkstreamsComponent,
    WorkstreamComponent,
    WorkstreamItemComponent,
    WorkstreamUploadComponent,
    RecommendedSolutionsComponent,
    RecommendedSolutionsListComponent,
    ReferenceResourcesComponent,
    ReferenceResourcesListComponent,
    GenerateSspComponent,
    ItProfileTemplateEditComponent,
    PolicyComponent,
    SelectPolicyComponent,
    AddUrlComponent,
    AssessmentEditComponent,
    AssessmentComponent,
    ThirdPartyAssessmentComponent,
    TrainingResultComponent,
    InitializeTenantComponent,
    ListMessageComponent,
    DocumentSelectComponent,
    ApiKeysComponent,
    MapControlItemsComponent,
    MapWorkstreamItemsComponent,
    AzureComponent,
    OidcCallbackComponent,
    AssessmentFindingsComponent,
    AssessmentManagerListComponent,
    AssessmentManagerEditComponent,
    AssessmentManagerViewComponent,
    CloneProjectComponent,
    CloneSourceComponent,
    //TODO : Migration
    //QuillEditor,
    SanitizeHtmlPipe,
    AuditComponent,
    TenantPermissionsComponent,
    TenantPermissionsEditComponent,
    AssessmentAnswersComponent,
    MessageComponent,
    IntegrationsComponent,
    InviteSurveyUserComponent,
    AssessmentSummaryComponent,
    TrainingSummaryComponent,
    RiskComponent,
    SelectAuditTemplateComponent,
    MappingTemplateComponent,
    ScoringReportComponent,
    ConfirmComponent,
    SupportConsoleComponent,
    SupportLoginComponent,
    AskStringComponent,
    CidReportComponent,
    ImplementationControlStatementComponent,
    SelectImplementationControlStatementComponent,
    SelectMappingTemplateComponent,
    ActionItemTemplateComponent,
    SelectActionPlanTemplateComponent,
    LicenseComponent,
    CreatePoamTemplateComponent,
    AssessmentInviteComponent,
    AssessmentUserAnswersComponent,
    TrainingUserAnswersComponent,
    TrainingInviteComponent,
    TrainingsComponent,
    ProjectNewComponent,
    IncidentResponseComponent,
    EditIncidentResponseComponent,
    PromptComponent,
    ThemePalettePanelComponent,
    DictionaryComponent,
    EditDictionaryItemComponent,
    SharedResponsibilityReportComponent,
    ExportPolicyComponent,
    InqSsoComponent,
    GenerateCrmComponent,
    ImportSspComponent,
    MfaStatusComponent,
    SetupTotpComponent,
    SetupEmailMfaComponent,
    RequestListComponent,
    MultiLoginComponent,
    EditDocumentRequestComponent,
    UnsubscribeNotificationComponent,
    AddLinkReferenceResourceComponent,
    AddLinkRecommendedSolutionComponent,
    ReferenceResourceUploadComponent,
    RecommendedSolutionUploadComponent,
    ProjectPoliciesComponent,
    ProjectPoamsComponent,
    MyProfileComponent,
    UserPopupComponent,
    SapProfileComponent,
    SarProfileComponent,
    PasswordStrengthMeterComponent,
    PSMProgressBarDirective,
    QuillEditor,
    EditRiskComponent,
    AssessmentsComponent,
    IntegrationAppsComponent,
    EditIntegrationAppComponent,
    SchedulerComponent,
    IntegrationAppsOscalCatalogComponent,
    IntegrationAppsOscalProfileComponent,
    IntegrationAppsOscalProfileCategoryComponent,
    IntegrationAppsOscalImplementationComponent,
    IntegrationAppsOscalPoamComponent,
    IntegrationAppsOscalSapComponent,
    IntegrationAppsOscalSarComponent,
    IntegrationAppsPoamComponent,
    IntegrationAppsEvidenceComponent,
    ConMonProfileComponent,
    IntegrationAwsConfigComponent,
    IntegrationGenericComponent,
    IntegrationAwsCloudTrailComponent,
    PoamListComponent,
    SendProjectControlItemMessageComponent,
    ReplacePolicyTextComponent,
    EvidenceCentralComponent,
    EditEvidenceCentralComponent,
    InspectionProfilesComponent,
    InspectionResultsPopupComponent,
    GuidanceDialogComponent,
    TrustBuilderComponent,
    UploadFrameworkDialogComponent,
    AddCategoryDialogComponent,
    AddControlItemDialogComponent,
    EditActionItemSetComponent,
    EditImplementationControlStatementSetComponent,
    EditTrustReportDialogComponent,
    Viewer3dComponent,
    RizkListConfigComponent,
    SelectDictionaryItemComponent,
    GenericControlsComponent,
    RizkListReportComponent,
    GlobalPoamListComponent,
    SelectDeviceComponent,
    ViewFileComponent,
    SelectEvidenceCentralItemComponent,
    SelectEvidenceCentralItemComponent,
    GlobalDeviceListComponent,
    ImportOscalComponent,
    CveComponent,
    IntegrationS3ConfigComponent,
    GlobalProjectControlItemsComponent,
    FleetComponent,
    FleetsComponent,
    FleetsDashboardComponent,
    FleetDashboardDetailComponent,
    FleetDeviceMappingComponent,
    AddSignatureComponent,
    TrustBuilderInviteComponent,
    GlobalTrustReportComponent,
    DocumentFolderSelect,
    SelectDiagramPositionComponent,
    TrustBuilderViewComponent,
  ],
  imports: [
    BrowserModule /*.withServerTransition({appId: 'ng-cli-universal'})*/,
    BrowserAnimationsModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    ToastModule,
    MatTooltipModule,
    MatToolbarModule,
    MatListModule,
    MatDialogModule,
    MatStepperModule,
    TableModule,
    DatePickerModule,
    DynamicDialogModule,
    MultiSelectModule,
    MatSelectModule,
    MatMenuModule,
    MatInputModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatTabsModule,
    ToastModule,
    TreeTableModule,
    MatRadioModule,
    MatFormFieldModule,
    TooltipModule,
    //TODO : Migration
    //EmbeddedMediaModule,
    ReactiveFormsModule,
    DialogModule,
    TooltipModule,
    SelectModule,
    SplitterModule,
    MatSlideToggleModule,
    MatIconModule,
    MatChipsModule,
    MatButtonModule,
    MatCardModule,
    CheckboxModule,
    TreeModule.forRoot(),
    StoreModule.forRoot({}),
    TranslateModule.forRoot(),
    FileManagerModule.forRoot(fileManagerConfiguration),
    ConfirmationPopoverModule.forRoot({
      confirmButtonType: 'danger',
    }),
    routing,
    MatButtonToggleModule,
    EffectsModule.forRoot(),
    MatNativeDateModule,
    ButtonModule,
    CardModule,
    ChartModule,
    PanelMenuModule,
    ColorPickerModule,
    NgxColorsModule,
    DividerModule,
    RadioButtonModule,
    DialogModule,
    DragDropModule,
    YouTubePlayerModule,
    FileSelectDirective,
    //PdfJsViewerModule,
  ],
  providers: [
    provideAnimations(),
    {provide: CSP_NONCE, useValue: 'temporaryNonRandomForAngular'},
    {provide: APP_BASE_HREF, useValue: '/'},
    [{provide: HTTP_INTERCEPTORS, useClass: HttpRegInterceptor, multi: true}],
    AuthGuard,
    AlertService,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {appearance: 'fill'},
    },
    {provide: FileManagerApiService, useClass: FileManagerBackendApiService1},
    providePrimeNG({
      theme: {
        preset: Aura
      }
    }),
    AuthenticationService,
    UserService,
    ProjectService,
    IntegrationsService,
    ProjectControlItemService,
    ComplianceAreaService,
    FileBlobService,
    ControlItemsService,
    TenantService,
    UploadService,
    ClientProfileService,
    WorkstreamService,
    NotificationService,
    BulletinService,
    MatIconRegistry,
    IndexConfigService,
    PasswordStrengthMeterService,
    AccessRequestService,
    LogService,
    ActionItemService,
    ApiKeyService,
    AssessmentService,
    AssessmentManagerService,
    AuditService,
    DictionaryService,
    DocumentRequestService,
    FileManagerBackendApiService,
    ImplementationControlStatementService,
    IncidentResponseService,
    MappingService,
    MfaService,
    PolicyService,
    RecommendedSolutionService,
    ReferenceResourceService,
    RiskService,
    SapProfileService,
    SarProfileService,
    SignalRService,
    TenantPermissionsService,
    TerminalService,
    TrainingService,
    OidcService,
    ConMonProfileService,
    DialogService,
    EvidenceCentralService,
    InspectionProfileService,
    GenericControlService,
    provideHttpClient(
      withXsrfConfiguration({ cookieName: 'X-XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' })
    ),
    provideHttpClient(
      withInterceptors([apiErrorInterceptor])
    ),
    CveService
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  static injector: Injector;

  constructor(public injector: Injector) {
    AppModule.injector = injector;
  }
}
